import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";

// react-toastify
import { toast as normalToast } from "react-toastify";

/* ======================================================
   BLOCKED AUTO MESSAGES - Add any message you want to block
====================================================== */
const BLOCKED_AUTO_MESSAGES = [
  "data retrieved successfully",
  "attendance data retrieved successfully",
  "fetched successfully",
  "loaded successfully",
  "get successfully",
  "record retrieved",
  "records retrieved",
  "data fetched",
];

/* ======================================================
   GLOBAL FLAG + ROOT SETUP
====================================================== */
if (typeof window !== "undefined") {
  if (window.__USE_CUSTOM_TOAST === undefined) {
    window.__USE_CUSTOM_TOAST = true;
  }

  if (!document.getElementById("global-toast-root")) {
    const root = document.createElement("div");
    root.id = "global-toast-root";
    document.body.appendChild(root);
  }
}

/* ======================================================
   CONTEXT
====================================================== */
const GlobalToastContext = createContext(null);

// SAFE hook - won't throw error
export const useGlobalToast = () => {
  const ctx = useContext(GlobalToastContext);

  if (!ctx) {
    console.warn("⚠️ Toast provider not found, using window.toast fallback");

    return {
      success: (msg) => {
        if (window.toast?.success) {
          window.toast.success(msg);
        } else {
          console.log("✅", msg);
        }
      },
      error: (msg) => {
        if (window.toast?.error) {
          window.toast.error(msg);
        } else {
          console.error("❌", msg);
        }
      },
      warning: (msg) => {
        if (window.toast?.warning) {
          window.toast.warning(msg);
        } else {
          console.warn("⚠️", msg);
        }
      },
      info: (msg) => {
        if (window.toast?.info) {
          window.toast.info(msg);
        } else {
          console.info("ℹ️", msg);
        }
      },
      dismiss: () => {
        if (window.toast?.dismiss) {
          window.toast.dismiss();
        }
      },
    };
  }

  return ctx;
};

// Alias
export const useToast = useGlobalToast;

/* ======================================================
   TOAST UI
====================================================== */
const ToastMessage = ({ message, type, title, onClose, isDarkMode }) => {
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(onClose, 3000);
    return () => clearTimeout(timerRef.current);
  }, [onClose]);

  const config = {
    success: { Icon: CheckCircleIcon, bg: "#4CAF50" },
    error: { Icon: ErrorIcon, bg: "#F44336" },
    warning: { Icon: WarningIcon, bg: "#FF9800" },
    info: { Icon: InfoIcon, bg: "#2196F3" },
  }[type];

  // Add CSS animations
  useEffect(() => {
    if (!document.getElementById("toast-animations")) {
      const style = document.createElement("style");
      style.id = "toast-animations";
      style.textContent = `
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(-20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes toastOut {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to { opacity: 0; transform: translateY(-20px) scale(0.95); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return ReactDOM.createPortal(
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 999999,
        animation: "toastIn 0.3s",
      }}
    >
      <div
        style={{
          width: 420,
          padding: 32,
          borderRadius: 20,
          background: isDarkMode
            ? "rgb(51, 50, 50)"
            : "rgba(255, 255, 255, 1)",
          color: isDarkMode ? "#ffffff94" : "#000",
          textAlign: "center",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: 70,
            height: 70,
            borderRadius: "50%",
            background: config.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <config.Icon style={{ fontSize: 36, color: "#fff" }} />
        </div>

        <h3 style={{ marginBottom: 8 }}>{title}</h3>
        <p style={{ margin: 0 }}>{message}</p>

        <button
          onClick={onClose}
          style={{
            marginTop: 20,
            padding: "8px 24px",
            background: config.bg,
            color: "white",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          OK
        </button>
      </div>
    </div>,
    document.getElementById("global-toast-root")
  );
};

/* ======================================================
   PROVIDER - AUTO CLEARS OLD TOAST
====================================================== */
export const GlobalToastProvider = ({ children }) => {
  const [currentToast, setCurrentToast] = useState(null);
  const timeoutRef = useRef(null);

  const themeStore = useSelector((state) => state.theme);
  const isDarkMode =
    themeStore?.theme === "dark" || themeStore?.theme === undefined;

  // Clear current toast
  const clearToast = () => {
    setCurrentToast(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Show new toast (automatically clears old one)
  const showToast = (
    message,
    type = "success",
    title = type.charAt(0).toUpperCase() + type.slice(1),
    delay = 0
  ) => {
       console.log("🔔 Toast called:", { message, type }); 
    // ✅ Block all automated GET/fetch success messages
    const isBlocked = BLOCKED_AUTO_MESSAGES.some((blocked) =>
      message?.toLowerCase().includes(blocked)
    );

    

    const useCustom = window.__USE_CUSTOM_TOAST === true;

    if (!useCustom) {
      normalToast.dismiss();
      setTimeout(() => {
        normalToast[type](message);
      }, delay);
      return;
    }

    // Clear old toast
    clearToast();

    const toastId = Date.now();

    // Delay showing toast if needed
    setTimeout(() => {
      setCurrentToast({ id: toastId, message, type, title });

      // Auto close after 3 sec
      timeoutRef.current = setTimeout(() => {
        clearToast();
      }, 3000);
    }, delay);
  };

  // Toast methods with auto-clear
  const toastMethods = {
    success: (msg, delay) => showToast(msg, "success", "Success", delay),
    error: (msg, delay) => showToast(msg, "error", "Error", delay),
    warning: (msg, delay) => showToast(msg, "warning", "Warning", delay),
    info: (msg, delay) => showToast(msg, "info", "Info", delay),
    dismiss: clearToast,
  };

  // Set globally
  useEffect(() => {
    window.toast = toastMethods;
    console.log("✅ Global Toast Ready");

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <GlobalToastContext.Provider value={toastMethods}>
      {children}
      {currentToast && (
        <ToastMessage
          key={currentToast.id}
          {...currentToast}
          onClose={clearToast}
          isDarkMode={isDarkMode}
        />
      )}
    </GlobalToastContext.Provider>
  );
};

export default GlobalToastProvider;